import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reviewAPI, feedbackAPI } from '@/services/api';
import { CommentSection } from './CommentSection';
import {
  Mail,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  Edit2,
  Send,
  Loader2,
  Sparkles,
  RefreshCw,
  Package,
} from 'lucide-react';

const LONG_BODY_CHARS = 1500;

const STATUS_STYLES: Record<string, { label: string; dot: string; pill: string }> = {
  pending_review: { label: 'Pending review', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-800 border-amber-200' },
  approved: { label: 'Approved', dot: 'bg-blue-500', pill: 'bg-blue-50 text-blue-800 border-blue-200' },
  sent: { label: 'Sent', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', dot: 'bg-red-500', pill: 'bg-red-50 text-red-800 border-red-200' },
  closed: { label: 'Closed', dot: 'bg-slate-400', pill: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Some bodies are stored as HTML; the ticket view shows them as plain text.
function cleanBodyContent(content: string): string {
  if (!content) return '';

  let text = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/li>/gi, '\n');

  text = text.replace(/<[^>]+>/g, '');

  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, '/');

  return text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function initials(name: string): string {
  const parts = (name || '').split(/[\s._-]+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]).join('') || '?').toUpperCase();
}

function humanize(value: string): string {
  const text = (value || '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function copyToClipboard(text: string) {
  if (!text) return;
  if (!navigator.clipboard) {
    toast.error('Copying is not available in this browser');
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Could not copy')
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        copyToClipboard(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
      className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
      title={label}
      aria-label={label}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function InfoRow({ label, children, stacked = false }: { label: string; children: React.ReactNode; stacked?: boolean }) {
  return (
    <div className={`px-3 py-2.5 ${stacked ? 'space-y-0.5' : 'flex items-center justify-between gap-3'}`}>
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide shrink-0 block">{label}</span>
      <div className={`min-w-0 text-xs text-slate-800 ${stacked ? '' : 'text-right'} [overflow-wrap:anywhere]`}>{children}</div>
    </div>
  );
}

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  const [orderInfoExpanded, setOrderInfoExpanded] = useState(true);
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [userFeedback, setUserFeedback] = useState<'upvote' | 'downvote' | null>(null);
  const [showRegenerateBox, setShowRegenerateBox] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const presetPrompts = [
    "Make response more polite & empathetic",
    "Offer a replacement or 10% discount",
    "Ask customer to provide photos of damaged item",
    "Keep response short, clear & direct",
    "Translate response to English"
  ];

  const { data: email, isLoading, isError } = useQuery({
    queryKey: ['emailDetail', id],
    queryFn: async () => {
      const response = await reviewAPI.getById(id!);
      const data = response.data;
      const entities = data.extracted_entities || {};
      const jtl = data.jtl_order_data || entities.jtl_order_data || {};
      const buyerName = jtl.buyer_name || entities.customer_name || entities.buyer_name || (data.from_address ? data.from_address.split('@')[0] : 'Customer');
      const platform = entities.platform || data.platform || 'unknown';
      return {
        id: data.id,
        customer_name: buyerName,
        from_address: data.from_address,
        channel: platform === 'amazon' ? 'Amazon' : (platform === 'ebay' ? 'eBay' : 'eDesk - Website'),
        country_flag: data.from_address?.endsWith('.de') ? '🇩🇪' : (data.from_address?.endsWith('.ie') ? '🇮🇪' : '🇬🇧'),
        subject: data.subject || `Inquiry from ${buyerName}`,
        body: data.body || '',
        category: data.category || 'general',
        created_at: new Date(data.received_at || data.created_at || Date.now()).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) + ' CET',
        ticket_number: data.id.substring(0, 6),
        response_text: data.response_text || 'AI Response processing...',
        edited_response: data.edited_response || null,
        response_status: data.response_status,
        // Order details come only from JTL-Wawi, as delivered by the JTL poller
        order_id: entities.order_id || entities.extracted_order_id || jtl.amazon_order_id || null,
        product_name: jtl.product_name || jtl.product?.name || null,
        asin: jtl.asin || null,
        buyer_email: jtl.buyer_email || null,
        order_status: jtl.order_status || null,
        order_date: jtl.order_date ? new Date(jtl.order_date).toLocaleDateString('de-DE') : null,
        tracking_number: jtl.tracking_number || jtl.tracking?.number || null,
        carrier: jtl.carrier || jtl.tracking?.carrier || null,
        sku: jtl.sku || jtl.product?.sku || null,
        jtl_order_id: jtl.internal_order_number || null,
        quantity: jtl.quantity ?? null,
        fulfillment_type: typeof jtl.is_fba === 'boolean' ? (jtl.is_fba ? 'FBA (Amazon)' : 'Merchant (FBM)') : null,
      };
    },
    enabled: !!id,
    refetchInterval: 3000,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await reviewAPI.approveAndSend(id!, 'Admin');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Response sent to customer!');
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
      navigate('/emails');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to send response');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (newText: string) => {
      const res = await reviewAPI.updateResponse(id!, newText);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Response updated');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['emailDetail', id] });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: async (type: 'upvote' | 'downvote') => {
      await feedbackAPI.submit(id!, type);
      setUserFeedback(type);
    },
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: async (promptText?: string) => {
      const res = await reviewAPI.regenerate(id!, promptText);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'New AI response generated successfully!');
      setShowRegenerateBox(false);
      setCustomPrompt('');
      queryClient.invalidateQueries({ queryKey: ['emailDetail', id] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to regenerate AI response');
    },
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white h-full">
        <div className="flex items-center space-x-2 text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>Loading ticket details from database...</span>
        </div>
      </div>
    );
  }

  if (isError || !email) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white h-full text-slate-500">
        <p className="text-base font-semibold text-slate-700 mb-2">Ticket not found</p>
        <button
          onClick={() => navigate('/emails')}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded font-medium hover:bg-blue-700"
        >
          Return to Mailbox
        </button>
      </div>
    );
  }

  const status = STATUS_STYLES[email.response_status] || {
    label: humanize(email.response_status || 'New ticket'),
    dot: 'bg-slate-400',
    pill: 'bg-white text-slate-700 border-slate-200',
  };
  const bodyText = cleanBodyContent(email.body);
  const bodyIsLong = bodyText.length > LONG_BODY_CHARS;
  const draftText = email.edited_response || email.response_text;
  const alreadySent = email.response_status === 'sent';

  return (
    <div className="flex flex-col h-full min-w-0 bg-white select-none overflow-hidden">
      <div className="flex-1 flex min-w-0 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-slate-50/60">
          <div className="max-w-5xl mx-auto p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${status.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                  <span className="inline-flex items-center border border-slate-200 bg-white rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                    {humanize(email.category)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                    Ticket #{email.ticket_number}
                    <CopyButton value={email.id} label="Copy ticket ID" />
                  </span>
                </div>
                <h1 className="text-lg font-bold text-slate-900 tracking-tight leading-snug [overflow-wrap:anywhere]">
                  {email.subject}
                </h1>
              </div>

              <span className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-[11px] font-semibold px-2.5 py-1 rounded-md shadow-sm">
                <span>{email.country_flag}</span>
                <span>{email.channel}</span>
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                    {initials(email.customer_name)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{email.customer_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{email.from_address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium shrink-0">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>{email.created_at}</span>
                </div>
              </div>

              <div className="px-5 py-4">
                <div
                  className={`text-sm text-slate-800 leading-relaxed font-sans whitespace-pre-line [overflow-wrap:anywhere] select-text ${
                    bodyIsLong && !bodyExpanded ? 'max-h-80 overflow-hidden [mask-image:linear-gradient(to_bottom,black_75%,transparent)]' : ''
                  }`}
                >
                  {bodyText || <span className="text-slate-400 italic">This message has no text.</span>}
                </div>
                {bodyIsLong && (
                  <button
                    onClick={() => setBodyExpanded(!bodyExpanded)}
                    className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    {bodyExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {bodyExpanded ? 'Show less' : 'Show full message'}
                  </button>
                )}
              </div>
            </div>

            <div className="bg-[#F0F6FF] border border-blue-100 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold text-blue-900">Reply</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 uppercase tracking-wider border border-blue-200 bg-white px-1.5 py-0.5 rounded">
                    <Sparkles className="w-3 h-3" />
                    AI draft
                  </span>
                  {email.edited_response && (
                    <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider border border-slate-200 bg-white px-1.5 py-0.5 rounded">
                      Edited
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {!draftCollapsed && (
                    <>
                      <button
                        onClick={() => feedbackMutation.mutate('upvote')}
                        className={`p-1.5 rounded-md transition-colors ${
                          userFeedback === 'upvote' ? 'text-blue-600 bg-blue-100' : 'text-slate-400 hover:text-slate-700 hover:bg-white'
                        }`}
                        title="Helpful AI response"
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => feedbackMutation.mutate('downvote')}
                        className={`p-1.5 rounded-md transition-colors ${
                          userFeedback === 'downvote' ? 'text-red-600 bg-red-50' : 'text-slate-400 hover:text-slate-700 hover:bg-white'
                        }`}
                        title="Unhelpful AI response"
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                      <CopyButton value={draftText} label="Copy reply" />
                      <button
                        onClick={() => {
                          setIsEditing(!isEditing);
                          setEditedResponse(draftText);
                          if (showRegenerateBox) setShowRegenerateBox(false);
                        }}
                        className={`p-1.5 rounded-md transition-colors ${
                          isEditing ? 'text-blue-600 bg-blue-100' : 'text-slate-400 hover:text-slate-700 hover:bg-white'
                        }`}
                        title="Edit response"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setShowRegenerateBox(!showRegenerateBox);
                          if (isEditing) setIsEditing(false);
                        }}
                        className={`ml-1 px-2.5 py-1 rounded-md transition-colors flex items-center gap-1 border text-xs font-semibold ${
                          showRegenerateBox
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-200'
                        }`}
                        title="Regenerate response with AI instructions"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                        <span>Regenerate</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setDraftCollapsed(!draftCollapsed)}
                    className="ml-1 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-white transition-colors"
                    title={draftCollapsed ? 'Expand reply' : 'Collapse reply'}
                  >
                    {draftCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {!draftCollapsed && (
                <>
                  <div className="bg-white border border-slate-200/80 rounded-lg shadow-sm mb-3 relative">
                    {regenerateMutation.isPending && (
                      <div className="absolute inset-0 z-10 rounded-lg bg-white/80 backdrop-blur-[1px] flex items-center justify-center">
                        <div className="flex items-center gap-2 text-xs font-semibold text-indigo-700">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Writing a new reply...</span>
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <div className="p-4 space-y-3">
                        <textarea
                          value={editedResponse}
                          onChange={(e) => setEditedResponse(e.target.value)}
                          rows={10}
                          autoFocus
                          className="w-full border border-slate-200 rounded-md p-3 text-sm leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-sans resize-y select-text"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-slate-400">{editedResponse.length} characters</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setIsEditing(false)}
                              className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => updateMutation.mutate(editedResponse)}
                              disabled={updateMutation.isPending || !editedResponse.trim()}
                              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {updateMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Save Draft
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-5 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] select-text">
                        {draftText}
                      </div>
                    )}

                    {showRegenerateBox && (
                      <div className="mx-4 mb-4 bg-indigo-50/50 p-3.5 rounded-lg border border-indigo-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-950">
                            <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                            <span>Custom AI Prompt / Instructions</span>
                          </div>
                          <span className="text-[10px] text-slate-500 font-normal">Optional instruction for AI</span>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-2.5">
                          {presetPrompts.map((preset, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setCustomPrompt(preset)}
                              className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                                customPrompt === preset
                                  ? 'bg-indigo-600 text-white border-indigo-600 font-medium'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                              }`}
                            >
                              {preset}
                            </button>
                          ))}
                        </div>

                        <textarea
                          value={customPrompt}
                          onChange={(e) => setCustomPrompt(e.target.value)}
                          placeholder="Type custom instructions for AI (e.g. 'Tell customer package was delayed by DHL, offer 10% refund')..."
                          rows={2}
                          className="w-full border border-indigo-200 rounded-md p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-sans bg-white select-text"
                        />

                        <div className="flex items-center justify-between mt-2.5">
                          <button
                            onClick={() => {
                              setShowRegenerateBox(false);
                              setCustomPrompt('');
                            }}
                            className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-800"
                          >
                            Cancel
                          </button>

                          <button
                            onClick={() => regenerateMutation.mutate(customPrompt || undefined)}
                            disabled={regenerateMutation.isPending}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-1.5 rounded-md shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                          >
                            {regenerateMutation.isPending ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Generating response...</span>
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3.5 h-3.5" />
                                <span>{customPrompt ? 'Generate with Custom Prompt' : 'Regenerate Answer'}</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="text-[11px] text-slate-500 truncate min-w-0">
                      To <span className="font-medium text-slate-700">{email.from_address}</span>
                    </span>
                    <button
                      onClick={() => sendMutation.mutate()}
                      disabled={sendMutation.isPending || isEditing || alreadySent}
                      title={isEditing ? 'Save your edits before sending' : alreadySent ? 'This reply was already sent' : 'Send reply to customer'}
                      className="bg-[#2563EB] hover:bg-blue-700 text-white text-xs font-semibold px-5 py-2 rounded-md shadow-sm hover:shadow transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    >
                      {sendMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : alreadySent ? <Check className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                      <span>{sendMutation.isPending ? 'Sending...' : alreadySent ? 'Sent' : 'Send'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <aside className="w-[280px] bg-white border-l border-slate-200 flex flex-col p-4 shrink-0 overflow-y-auto overflow-x-hidden">
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                {initials(email.customer_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold text-slate-900 text-sm truncate">{email.customer_name}</span>
                  <CopyButton value={email.customer_name} label="Copy name" />
                </div>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs text-slate-500 truncate">{email.from_address}</span>
                  <CopyButton value={email.from_address} label="Copy email" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
              <span className="font-medium text-slate-500">Channel</span>
              <span className="flex items-center gap-1 font-semibold text-slate-800">
                <span>{email.country_flag}</span>
                <span>{email.channel}</span>
              </span>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => setOrderInfoExpanded(!orderInfoExpanded)}
              className="w-full flex items-center justify-between font-bold text-xs text-slate-700 tracking-wider uppercase hover:text-slate-900 mb-3"
            >
              <span className="flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-slate-400" />
                Order info
              </span>
              <ChevronDown
                className={`w-4 h-4 text-slate-400 transition-transform ${
                  orderInfoExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {orderInfoExpanded && (
              email.order_id ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50/70 divide-y divide-slate-200 overflow-hidden">
                  <InfoRow label="Order ID">
                    <span className="inline-flex items-center gap-0.5 font-mono font-semibold">
                      {email.order_id}
                      <CopyButton value={email.order_id} label="Copy order ID" />
                    </span>
                  </InfoRow>
                  {email.product_name && (
                    <InfoRow label="Product" stacked>
                      <span className="leading-snug">{email.product_name}</span>
                    </InfoRow>
                  )}
                  {(email.asin || email.sku) && (
                    <InfoRow label={email.asin ? 'ASIN' : 'SKU'}>
                      <span className="font-mono text-slate-700">{email.asin || email.sku}</span>
                    </InfoRow>
                  )}
                  {email.order_status && (
                    <InfoRow label="Status">
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">{email.order_status}</span>
                    </InfoRow>
                  )}
                  {email.order_date && (
                    <InfoRow label="Ordered">
                      <span className="font-medium">{email.order_date}</span>
                    </InfoRow>
                  )}
                  {email.tracking_number && (
                    <InfoRow label="Tracking" stacked>
                      <span className="flex items-center justify-between gap-1">
                        <span className="font-mono text-blue-700 [overflow-wrap:anywhere]">{email.tracking_number}</span>
                        <CopyButton value={email.tracking_number} label="Copy tracking number" />
                      </span>
                    </InfoRow>
                  )}
                  {email.carrier && (
                    <InfoRow label="Carrier">
                      <span className="font-medium">{email.carrier}</span>
                    </InfoRow>
                  )}
                  {email.fulfillment_type && (
                    <InfoRow label="Fulfillment">
                      <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">{email.fulfillment_type}</span>
                    </InfoRow>
                  )}
                  {email.buyer_email && email.buyer_email !== email.from_address && (
                    <InfoRow label="Buyer Email" stacked>
                      <span className="text-slate-700">{email.buyer_email}</span>
                    </InfoRow>
                  )}
                  {email.jtl_order_id && (
                    <InfoRow label="JTL Ref">
                      <span className="font-mono text-slate-700">{email.jtl_order_id}</span>
                    </InfoRow>
                  )}
                </div>
              ) : (
                <div className="bg-[#F0F7FF] border border-blue-100 rounded-lg p-3 text-[11px] text-slate-700 leading-relaxed">
                  <span className="flex items-center gap-1.5 font-semibold text-slate-900">
                    <Mail className="w-3.5 h-3.5 text-blue-500" />
                    No order linked
                  </span>
                  <p className="mt-1 text-slate-500">Order info will appear here once an order ID is extracted from this email.</p>
                </div>
              )
            )}
          </div>

          <div className="border-t border-slate-200 pt-4 mt-4">
            <CommentSection emailId={email.id} />
          </div>
        </aside>
      </div>
    </div>
  );
}
